import { getRequiredEnvVar } from '@shared/utils/validate-env';
import { getValidAuthToken } from '@services/auth/auth';
import { buildPortalIntegrationHeaders } from '@shared/utils/build-portal-integration-headers';
import { withAntiBotRetry as _withAntiBotRetry } from '@shared/utils/with-antibot-retry';

export { isAntiBotError } from '@shared/utils/with-antibot-retry';

export const API_BASE = getRequiredEnvVar('REACT_APP_API_URL');

/**
 * Request headers for a safe (GET) read: the bearer token and nothing else.
 *
 * The backend enforces anti-bot only on state-changing methods
 * (`OIDCClientProtectedRoute.SAFE_METHODS`), so minting a reCAPTCHA token for
 * a read is pure overhead -- and the pending-publications poll reads every few
 * seconds, which meant a fresh token (and its round-trips to Google) per tick.
 *
 * @param token - The current bearer token, if any.
 * @returns An `Authorization` header, or an empty map when signed out.
 */
export function readHeadersFromToken(token: string | null | undefined): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Builds auth-only headers for a safe (GET) portal-integration read.
 *
 * @returns Headers carrying the bearer token, without an `X-Anti-Bot` token.
 */
export async function buildReadHeaders(): Promise<Record<string, string>> {
  return readHeadersFromToken(await getValidAuthToken());
}

/**
 * Builds portal-integration headers and requires a non-empty `X-Anti-Bot` token.
 *
 * @param opts Optional `{ reload: true }` after AntiBotInvalidTokenError.
 */
export function buildHeaders(opts?: { reload?: boolean }): Promise<Record<string, string>> {
  return buildPortalIntegrationHeaders('portal_integration', opts);
}

/**
 * Executes an axios call with anti-bot headers and retries once with a fresh
 * token if the server responds with 403 AntiBotInvalidTokenError.
 */
export function withAntiBotRetry<T>(
  fn: (headers: Record<string, string>) => Promise<T>,
): Promise<T> {
  return _withAntiBotRetry(fn, buildHeaders);
}
